import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { LuX } from "react-icons/lu";
import { cn } from "./cn";

type DialogSize = "sm" | "md" | "lg";
type DialogPlacement = "center" | "right";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  size?: DialogSize;
  placement?: DialogPlacement;
  /** Bloqueia fechar (Esc, fundo, X) — ex.: enquanto salva. */
  closeDisabled?: boolean;
  children?: React.ReactNode;
}

// Pilha dos diálogos abertos, do mais antigo ao mais recente.
const openDialogs: React.RefObject<HTMLDivElement>[] = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Diálogos pequenos viram bottom sheet no celular; os demais ocupam a tela
// inteira no celular. A partir de 640px, todos viram cartão (ou painel lateral).
const CONTAINER: Record<DialogPlacement, Record<DialogSize, string>> = {
  center: {
    sm: "items-end justify-center sm:items-center sm:p-4",
    md: "items-stretch justify-center sm:items-center sm:p-4",
    lg: "items-stretch justify-center sm:items-center sm:p-4",
  },
  right: { sm: "justify-end", md: "justify-end", lg: "justify-end" },
};

const PANEL: Record<DialogPlacement, Record<DialogSize, string>> = {
  center: {
    sm: "w-full max-h-[90vh] rounded-t-panel max-sm:animate-slide-up sm:max-w-sm sm:rounded-panel sm:animate-scale-in",
    md: "h-full w-full max-sm:animate-slide-up sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-panel sm:animate-scale-in",
    lg: "h-full w-full max-sm:animate-slide-up sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-panel sm:animate-scale-in",
  },
  right: {
    sm: "h-full w-full sm:max-w-sm",
    md: "h-full w-full sm:max-w-lg",
    lg: "h-full w-full sm:max-w-2xl",
  },
};

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  description,
  footer,
  size = "md",
  placement = "center",
  closeDisabled = false,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  // Refs para os handlers não reexecutarem o efeito (e roubarem o foco) a cada render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closeDisabledRef = useRef(closeDisabled);
  closeDisabledRef.current = closeDisabled;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    // Foca o primeiro campo do corpo; sem campo, o próprio painel.
    const firstField = panel?.querySelector("[data-dialog-body]")?.querySelector<HTMLElement>(FOCUSABLE);
    (firstField ?? panel)?.focus();

    // Com diálogos empilhados (confirmação sobre um painel), só o de cima responde ao teclado.
    // A rolagem trava no primeiro e só destrava quando o último fecha, em qualquer ordem.
    openDialogs.push(panelRef);
    document.body.style.overflow = "hidden";

    const handleKey = (e: KeyboardEvent) => {
      if (openDialogs[openDialogs.length - 1] !== panelRef) return;
      if (e.key === "Escape" && !closeDisabledRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      // Mantém o Tab dentro do diálogo.
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("keydown", handleKey);
      openDialogs.splice(openDialogs.indexOf(panelRef), 1);
      if (openDialogs.length === 0) document.body.style.overflow = "";
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  // Acima dos overlays das telas antigas (z-index 10000), que ainda podem estar abertos por baixo.
  return createPortal(
    <div className={cn("fixed inset-0 z-[10050] flex", CONTAINER[placement][size])}>
      <div
        className="absolute inset-0 bg-fg/40 animate-fade-in"
        onClick={closeDisabled ? undefined : onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          "relative z-10 flex flex-col overflow-hidden bg-surface shadow-modal focus:outline-none",
          placement === "right" && "animate-slide-in-right",
          PANEL[placement][size]
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 id={titleId} className="text-heading font-semibold text-fg">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-caption text-fg-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Fechar"
            className="-mr-3 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-control text-fg-muted hover:bg-surface-hover hover:text-fg focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
          >
            <LuX size={18} aria-hidden />
          </button>
        </header>

        <div data-dialog-body className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {children}
        </div>

        {footer && (
          <footer
            className={cn(
              "flex flex-col-reverse gap-2 border-t border-line px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-6 sm:pb-3",
              // O diálogo pequeno (384px) não comporta dois botões lado a lado: ficam empilhados.
              size !== "sm" && "sm:flex-row sm:flex-wrap sm:justify-end"
            )}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
};
